import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Label } from '@bingads-webui-react/primitive';
import { withValidation, withError } from '@bingads-webui-react/validation';
import { Stack, Dropdown, TextField } from '@bingads-webui-react/fabric-control';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { sampleSchema as schema } from './sample-schema';
import { Error } from './error';

const ValidatedInput = withError(withValidation(TextField));

function getStyles() {
  return {
    adjustmentInput: {
      marginBottom: 10,
    },
  };
}

const getErrorMessage = (selectedSymbol, adjustmentRate) => {
  if (!adjustmentRate) return _TL_('Conversion rate is required. Enter a percentage.');
  if ((adjustmentRate < 0) || isNaN(adjustmentRate) ||
    (selectedSymbol === 'increase' && adjustmentRate > 900) ||
    (selectedSymbol === 'decrease' && adjustmentRate > 90)) {
    return _TL_('Percentage is not valid. Enter a conversion rate between -90% and 900%.');
  }
  return _TL_('');
};

export function validateConversionRate(selectedSymbol, adjustmentRate) {
  return _.isEmpty(getErrorMessage(selectedSymbol, adjustmentRate));
}

function SeasonalityAdjustmentRateInput(props) {
  const {
    classes, i18n, onValidate, setAdjustmentRate, setSelectedSymbol, selectedSymbol, adjustmentRate, showValidationError,
  } = props;

  function onChangePercentageInput(result) {
    onValidate(result.isValid);
    setAdjustmentRate(result.data.AdjustmentPercentage);
  }

  const onSymbolChange = (event, option) => {
    setSelectedSymbol(option.key);
  };

  const options = [
    {
      key: 'increase',
      text: i18n.getString(_TL_('Increase')),
    },
    {
      key: 'decrease',
      text: i18n.getString(_TL_('Decrease')),
    },
  ];

  const errorMessage = showValidationError ? i18n.getString(getErrorMessage(selectedSymbol, adjustmentRate)) : '';

  const stackTokens = { childrenGap: 10 };
  return (
    <div className={`adjustment-rate-select ${classes.adjustmentRateSelect}`}>
      <Label id="adjustment-rate-label">
        { i18n.getString(_TL_('Conversion rate adjustment')) }
        <HelpPopupInline
          topic="pop_BA_SeasonalityAdjustments_ConversionRate"
          ariaLabel={i18n.getString(_TL_('Click to learn more about {{item}}', { item: i18n.getString(_TL_('Conversion Rate Adjustment')) }))}
        />
      </Label>
      <Stack
        horizontal
        tokens={stackTokens}
      >
        <Dropdown
          selectedKey={selectedSymbol}
          onChange={onSymbolChange}
          options={options}
          ariaLabel={i18n.getString(_TL_('Select conversion rate adjustment sign'))}
        />
        <ValidatedInput
          validationProps={{ schema, onValidate: onChangePercentageInput, field: 'AdjustmentPercentage' }}
          className={`adjustment-rate-input ${classes.adjustmentInput}`}
          placeholder={i18n.getString(_TL_('Enter a percentage'))}
          value={adjustmentRate}
          suffix="%"
          ariaLabel={i18n.getString(_TL_('Rate adjustment'))}
        />
      </Stack>
      <Error message={errorMessage} />
    </div>
  );
}

SeasonalityAdjustmentRateInput.propTypes = {
  i18n: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({}).isRequired,
  setSelectedSymbol: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  setAdjustmentRate: PropTypes.func.isRequired,
  selectedSymbol: PropTypes.string.isRequired,
  adjustmentRate: PropTypes.string.isRequired,
  showValidationError: PropTypes.bool.isRequired,
};

const Wrapped = _.compose(
  withI18nConsumer,
  Comp => withDefaultStyles(Comp, getStyles)
)(SeasonalityAdjustmentRateInput);

export { Wrapped as SeasonalityAdjustmentRateInput };
